import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import { diffObjects } from 'utils';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { Box } from '@material-ui/core';
import { usersActions } from 'state/modules/users';
import { TextField, PhoneField } from 'components/FormFields';

const UpdateUserModal = ({ id, user, open, handleClick }) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [newEmail, setNewEmail] = useState(null);

  const UpdateUserSchema = Yup.object().shape({
    email: Yup.string()
      .email(i18n.t('common.invalid_email'))
      .required(i18n.t('common.required_field')),
    first_name: Yup.string().required(i18n.t('common.required_field')),
    last_name: Yup.string().required(i18n.t('common.required_field')),
    mobile_phone: Yup.string(),
    internal_id: Yup.string().max(25, i18n.t('common.max_value', { max_value: 25 })),
  });

  const { errors, fetchingUser, fetchedUserSuccess } = useSelector((state) => state.users);

  const initialValues = {
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    mobile_phone: user?.mobile_phone || '',
    internal_id: user?.internal_id || '',
  };

  useEffect(() => {
    dispatch(usersActions.resetUpdateUser());
  }, [dispatch]);

  useEffect(() => {
    if (fetchedUserSuccess) {
      if (newEmail) {
        dispatch(usersActions.fetchSendConfirmationLink(id, newEmail));
      }
      handleClick(true);
      dispatch(usersActions.resetUpdateUser());
    }
  }, [fetchedUserSuccess, newEmail, id, handleClick, dispatch]);

  const onSubmit = (values, { setErrors }) => {
    // Create payload based on dirty fields
    const payload = diffObjects(initialValues, values);
    if (payload.email) {
      setNewEmail(payload.email);
    } else {
      setNewEmail(null);
    }

    const { mobile_phone, phone_code, ...valuesNoMobile } = payload;
    if (mobile_phone) {
      if (mobile_phone === `+${phone_code}`) {
        return setErrors({ mobile_phone: i18n.t('common.required_field') });
      }
      delete payload.phone_code;
      dispatch(usersActions.fetchUpdateUser(id, payload));
    } else {
      dispatch(usersActions.fetchUpdateUser(id, valuesNoMobile));
    }
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onCancel={handleClick}
      onEnter={handleSubmit}
      enableCloseButton={false}
      loading={fetchingUser}
      title={i18n.t('users.update')}
      enterButtonLabel={i18n.t('users.update')}
    >
      <Formik
        innerRef={formRef}
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={UpdateUserSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="first_name"
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.first_name}
                label={i18n.t('fields.first_name')}
                data-cy="first_name-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="last_name"
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.last_name}
                label={i18n.t('fields.last_name')}
                data-cy="last_name-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                type="email"
                name="email"
                margin="none"
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.email}
                label={i18n.t('fields.email')}
                data-cy="email-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                margin="none"
                required={false}
                name="mobile_phone"
                label={i18n.t('fields.phone')}
                dialcodename="phone_code"
                asyncErrors={errors?.mobile_phone}
                component={user?.mobile_phone ? PhoneField : TextField}
                data-cy="mobile_phone-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="internal_id"
                required={false}
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.internal_id}
                label={i18n.t('customers.internal_id')}
                data-cy="internal_id-input"
              />
            </Box>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

UpdateUserModal.propTypes = {
  id: PropTypes.any,
  user: PropTypes.any,
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

UpdateUserModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default UpdateUserModal;
